import { useTranslation } from 'react-i18next';
import { ActionIcon, Box, Group, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconDownload, IconReceipt } from '@tabler/icons-react';
import SignatureBadge from '@/components/app/deals/SignatureBadge';
import NoNds from '@/components/common/NoNds';
import { useDownloadDocument } from '@/hooks/useDeals';
import { visibleInvoices } from '@/lib/dealInvoices';
import { formatMoney } from '@/lib/format';
import type { BargainStepsInfo, SignatureRole } from '@/types/deals';

// Переменная (не строковый литерал в JSX) — иначе jsx-a11y/aria-role примет role="AGENT" за ARIA-роль.
const AGENT_ROLE: SignatureRole = 'AGENT';

interface InvoicesReadonlyProps {
  readonly info: BargainStepsInfo;
}

// Read-only сводка счетов для пройденного шага «Счета за товар и логистику»: суммы без НДС + документ
// со статусом подписи. В отличие от живого InvoicesStep — без баннеров «ожидает подписи»/«формируется»
// (шаг уже пройден). Показываем строку, только если есть сумма или документ (иначе нечего показывать).
export default function InvoicesReadonly({ info }: InvoicesReadonlyProps) {
  const { t } = useTranslation();
  const download = useDownloadDocument();

  // Продавцу счета не показываем (бизнес-правило): счёт за товар был выставлен покупателю.
  if (info.isSeller === true) {
    return (
      <Text fz="sm" c="gray.8">
        {t('app.deals.invoices.sellerDone')}
      </Text>
    );
  }

  const invoices = visibleInvoices(info);

  if (invoices.length === 0) {
    return null;
  }

  return (
    <Stack gap="xs">
      {invoices.map((inv) => (
        <Group key={inv.key} justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
          <Group gap="sm" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
            <ThemeIcon size={32} radius="md" variant="light" color="gray">
              <IconReceipt size={18} />
            </ThemeIcon>
            <Box style={{ minWidth: 0 }}>
              <Text fz="sm" fw={500}>
                {t(inv.titleKey)}
              </Text>
              {inv.amount != null ? (
                <Text fz="sm" c="gray.7">
                  {formatMoney(inv.amount)} ₽
                  <NoNds />
                </Text>
              ) : null}
              {inv.doc?.signatures?.AGENT ? (
                <Box mt={4}>
                  <SignatureBadge role={AGENT_ROLE} info={inv.doc.signatures.AGENT} />
                </Box>
              ) : null}
            </Box>
          </Group>
          {inv.doc?.id != null ? (
            <Tooltip label={t('app.deals.docs.download')}>
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={t('app.deals.docs.download')}
                loading={download.isPending && download.variables === inv.doc.id}
                onClick={() => download.mutate(inv.doc!.id!)}
              >
                <IconDownload size={18} />
              </ActionIcon>
            </Tooltip>
          ) : null}
        </Group>
      ))}
    </Stack>
  );
}
